import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import {
  listAssessments,
  createAssessment,
  updateAssessment,
  deleteAssessment,
  publishAssessment,
  unpublishAssessment,
  listAssessmentAttempts,
  computeOrgAssessmentPerformance,
} from "../../../utils/assessmentHelpers"
import { generateLocalId } from "../../../utils/courseHelpers"

const ACCESS_OPTIONS = [
  { value: "open", label: "Open — any learner in the org" },
  { value: "assigned", label: "Assigned — specific learners only" },
]

const StatTile = ({ label, value }) => (
  <div className="p-4 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.06]">
    <p className="text-sm text-muted-foreground mb-1">{label}</p>
    <p className="text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

// Minimal question editor — question text + 4 options + which one is
// correct. Matches gradeQuiz's expected shape (q.id, q.correctOptionId,
// answers[q.id] === selected option id) with no external quiz-builder
// component to reuse, since none exists outside the course builder's
// internal module editor.
const QuestionEditor = ({ questions, onChange }) => {
  const addQuestion = () => {
    onChange([
      ...(questions || []),
      { id: generateLocalId(), text: "", options: [{ id: generateLocalId(), text: "" }, { id: generateLocalId(), text: "" }], correctOptionId: "" },
    ])
  }
  const updateQuestion = (qId, updates) => onChange(questions?.map((q) => (q.id === qId ? { ...q, ...updates } : q)))
  const removeQuestion = (qId) => onChange(questions?.filter((q) => q.id !== qId))
  const updateOption = (qId, optId, text) =>
    onChange(
      questions?.map((q) =>
        q.id === qId ? { ...q, options: q.options.map((o) => (o.id === optId ? { ...o, text } : o)) } : q
      )
    )
  const addOption = (qId) =>
    onChange(questions?.map((q) => (q.id === qId ? { ...q, options: [...q.options, { id: generateLocalId(), text: "" }] } : q)))

  return (
    <div className="space-y-4">
      {questions?.map((q, i) => (
        <div key={q.id} className="p-4 rounded-lg border border-border space-y-3">
          <div className="flex items-start gap-2">
            <span className="text-sm font-semibold text-muted-foreground shrink-0 mt-2">{i + 1}.</span>
            <Input
              placeholder="Question text"
              value={q.text}
              onChange={(e) => updateQuestion(q.id, { text: e?.target?.value })}
              className="flex-1"
            />
            <Button variant="ghost" size="icon" iconName="Trash2" iconSize={14} onClick={() => removeQuestion(q.id)} />
          </div>
          <div className="pl-6 space-y-2">
            {q.options?.map((opt) => (
              <label key={opt.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`correct-${q.id}`}
                  checked={q.correctOptionId === opt.id}
                  onChange={() => updateQuestion(q.id, { correctOptionId: opt.id })}
                />
                <Input
                  placeholder="Option text"
                  value={opt.text}
                  onChange={(e) => updateOption(q.id, opt.id, e?.target?.value)}
                  className="flex-1 h-8 text-sm"
                />
              </label>
            ))}
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => addOption(q.id)}>
              + Add option
            </button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" iconName="Plus" iconPosition="left" onClick={addQuestion}>
        Add Question
      </Button>
    </div>
  )
}

const AssessmentFormModal = ({ initialAssessment, onClose, onSave }) => {
  const [title, setTitle] = useState(initialAssessment?.title || "")
  const [description, setDescription] = useState(initialAssessment?.description || "")
  const [passingScore, setPassingScore] = useState(initialAssessment?.passingScore || 70)
  const [pointsReward, setPointsReward] = useState(initialAssessment?.pointsReward || 0)
  const [skillTagsInput, setSkillTagsInput] = useState(initialAssessment?.skillTags?.join(", ") || "")
  const [accessMode, setAccessMode] = useState(initialAssessment?.accessMode || "open")
  const [questions, setQuestions] = useState(initialAssessment?.questions || [])
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setError("")
    if (!title?.trim()) {
      setError("Title is required")
      return
    }
    if (!questions?.length) {
      setError("Add at least one question")
      return
    }
    setSaving(true)
    await onSave({
      title,
      description,
      passingScore: Number(passingScore) || 70,
      pointsReward: Number(pointsReward) || 0,
      skillTags: skillTagsInput.split(",").map((s) => s.trim()).filter(Boolean),
      accessMode,
      questions,
    })
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">{initialAssessment ? "Edit" : "New"} Assessment</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Title" placeholder="e.g. Blockchain Fundamentals Check" value={title} onChange={(e) => setTitle(e?.target?.value)} required />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Description (optional)</label>
              <textarea
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e?.target?.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Passing score (%)" type="number" min="0" max="100" value={passingScore} onChange={(e) => setPassingScore(e?.target?.value)} />
              <Input label="Points reward" type="number" min="0" value={pointsReward} onChange={(e) => setPointsReward(e?.target?.value)} />
            </div>

            <Input
              label="Skill tags (comma-separated)"
              placeholder="e.g. Blockchain, Smart Contracts"
              value={skillTagsInput}
              onChange={(e) => setSkillTagsInput(e?.target?.value)}
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Access</label>
              <Select options={ACCESS_OPTIONS} value={accessMode} onChange={setAccessMode} />
              <p className="text-xs text-muted-foreground mt-1">
                {accessMode === "assigned"
                  ? "Assign specific learners from the assessment's card after creating it."
                  : "Any learner in your organization will be able to take this once published."}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Questions</label>
              <QuestionEditor questions={questions} onChange={setQuestions} />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="default" fullWidth loading={saving}>Save</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const AssessmentsPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [assessments, setAssessments] = useState([])
  const [attemptCounts, setAttemptCounts] = useState({})
  const [orgStats, setOrgStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingAssessment, setEditingAssessment] = useState(null)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const loadData = async () => {
    setLoading(true)
    const [{ assessments: list }, stats] = await Promise.all([
      listAssessments(orgId),
      computeOrgAssessmentPerformance(orgId),
    ])
    setAssessments(list || [])
    setOrgStats(stats)

    const counts = await Promise.all(
      (list || []).map(async (a) => [a.assessmentId, (await listAssessmentAttempts(a.assessmentId)).attempts?.length || 0])
    )
    setAttemptCounts(Object.fromEntries(counts))
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const handleSave = async (payload) => {
    if (editingAssessment) {
      await updateAssessment(orgId, editingAssessment.assessmentId, payload)
    } else {
      await createAssessment({ orgId, ...payload })
    }
    setShowFormModal(false)
    setEditingAssessment(null)
    loadData()
  }

  const handleTogglePublish = async (assessment) => {
    if (assessment.status === "published") await unpublishAssessment(orgId, assessment.assessmentId)
    else await publishAssessment(orgId, assessment.assessmentId)
    loadData()
  }

  const handleDelete = (assessmentId) => {
    if (!window.confirm("Delete this assessment? Existing attempts are kept but the assessment can no longer be taken.")) return
    setPendingDeleteId(assessmentId)
  }

  const confirmDelete = async () => {
    await deleteAssessment(orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadData()
  }

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile label="Assessments Completed" value={orgStats?.totalCompleted?.toLocaleString() || 0} />
        <StatTile label="Average Score" value={`${orgStats?.avgScore || 0}%`} />
        <StatTile label="Pass Rate" value={`${orgStats?.passRate || 0}%`} />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{assessments?.length || 0} assessment{assessments?.length === 1 ? "" : "s"}</p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => { setEditingAssessment(null); setShowFormModal(true) }}>
          New Assessment
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading assessments...</div>
      ) : assessments?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ClipboardCheck" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No assessments yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Build a standalone assessment any learner can take, independent of a course.</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowFormModal(true)}>New Assessment</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {assessments?.map((assessment) => (
            <Card key={assessment.assessmentId} className="p-0 overflow-hidden gap-3 dark:bg-transparent">
              <CardContent className="pt-5 pb-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">{assessment.title}</h4>
                  <Badge variant={assessment.status === "published" ? "success" : "warning"} className="shrink-0">
                    {assessment.status === "published" ? "Published" : "Draft"}
                  </Badge>
                </div>
                <Badge variant="outline" className="w-fit">{assessment.accessMode === "open" ? "Open Access" : "Assigned"}</Badge>
                {assessment.skillTags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {assessment.skillTags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{tag}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{assessment.questions?.length || 0} questions</span>
                  <span>{attemptCounts[assessment.assessmentId] || 0} attempts</span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button variant="outline" size="sm" fullWidth onClick={() => handleTogglePublish(assessment)}>
                    {assessment.status === "published" ? "Unpublish" : "Publish"}
                  </Button>
                  <Button variant="ghost" size="icon" iconName="Pencil" iconSize={16} onClick={() => { setEditingAssessment(assessment); setShowFormModal(true) }} />
                  <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(assessment.assessmentId)} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showFormModal && (
        <AssessmentFormModal initialAssessment={editingAssessment} onClose={() => { setShowFormModal(false); setEditingAssessment(null) }} onSave={handleSave} />
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this assessment" onConfirm={confirmDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default AssessmentsPanel